import { leaveOrAttendanceRequestActions } from 'actions/profileNew';
import { Checkbox, Col, Modal, Row } from 'antd';
import { Button } from 'components/Button';
import { Heading } from 'components/Typography';
import moment from 'moment';
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';

const hasLeavePrivilege = (u) => {
        const p = u?.privilage || u?.privilege || u?.privilages;
        if (Array.isArray(p)) {
                return p.includes('SuperAdmin') || p.includes('LeaveManager');
        }
        if (typeof p === 'string') {
                return p.includes('SuperAdmin') || p.includes('LeaveManager');
        }
        return false;
};

const LeaveRequestsModal = ({ open, onClose, data, leaveOrAttendanceRequestActions, user, onActionSuccess }) => {
        const canPerformActions = hasLeavePrivilege(user);
        const [approveBtnLoader, setApproveBtnLoader] = useState(false);
        const [rejectBtnLoader, setRejectBtnLoader] = useState(false);
        const [selectedId, setSelectedId] = useState([]);

        useEffect(() => {
                setSelectedId([]);
        }, [open, data]);

        const successCallback = () => {
                setSelectedId([]);
                if (typeof onActionSuccess === 'function') {
                        onActionSuccess();
                } else {
                        window.location.reload();
                }
        };

        const requestList = Array.isArray(data?.requests) && data.requests.length > 0
                ? data.requests
                : (data?.startDate ? [{ id: data?.id, date: data?.startDate, status: data?.status, overAllStatus: data?.overAllStatus }] : []);

        // Approved / rejected dates can be changed only from tomorrow onwards.
        const isTodayOrPastDay = (dateStr) => {
                if (!dateStr) return false;
                return !moment(dateStr, ['YYYY-MM-DD', 'DD/MM/YYYY', moment.ISO_8601]).startOf('day').isAfter(moment().startOf('day'));
        };

        const getItemStatusStr = (item) => {
                const s = item?.overAllStatus ?? item?.overallStatus ?? item?.status ?? data?.overAllStatus ?? data?.overallStatus ?? data?.status ?? '';
                return String(s).trim().toLowerCase();
        };

        const isDateCheckboxDisabled = (item) => {
                const past = isTodayOrPastDay(item?.date || item?.startDate);
                const status = getItemStatusStr(item);
                // Today and past days with approved or rejected status cannot be modified
                return past && (status === 'approved' || status === 'rejected');
        };

        const isDateActionable = (item) => {
                const past = isTodayOrPastDay(item?.date || item?.startDate);
                const status = getItemStatusStr(item);
                if (past && (status === 'approved' || status === 'rejected')) {
                        return false;
                }
                return true;
        };

        // If all items are past days with approved or rejected status, we cannot change their status -> do not show buttons
        const actionableItems = requestList.filter(item => isDateActionable(item));
        const hasActionableDates = actionableItems.length > 0;

        const actionableStatuses = actionableItems.map(item => getItemStatusStr(item));
        const allApproved = actionableStatuses.length > 0 && actionableStatuses.every(s => s === 'approved');
        const allRejected = actionableStatuses.length > 0 && actionableStatuses.every(s => s === 'rejected');

        // Check the statuses of selected items
        const selectedItems = requestList.filter(item => selectedId.includes(item?.id));
        const selectedStatuses = selectedItems.map(item => getItemStatusStr(item));

        const allSelectedApproved = selectedStatuses.length > 0 && selectedStatuses.every(s => s === 'approved');
        const allSelectedRejected = selectedStatuses.length > 0 && selectedStatuses.every(s => s === 'rejected');

        // If user selected only rejected date(s) -> enable only approve button (reject is disabled)
        // If user selected only approved date(s) -> enable only reject button (approve is disabled)
        // If nothing is selected -> both buttons are disabled
        const isRejectDisabled = selectedId.length === 0 || allSelectedRejected || rejectBtnLoader;
        const isApproveDisabled = selectedId.length === 0 || allSelectedApproved || approveBtnLoader;

        const handleApprove = (commonId) => {
                const targetRequestIds = selectedId && selectedId.length > 0
                        ? selectedId
                        : (actionableItems.map(item => item?.id).filter(Boolean) || []);

                let params = {
                        action: 'Approved',
                        commonId: commonId,
                        requestIds: targetRequestIds,
                        requestType: 'Leave'
                };
                if (data?.userId || data?.user?.id) {
                        params.userId = data?.userId || data?.user?.id;
                }

                let succeeded = false;

                setApproveBtnLoader(true);
                leaveOrAttendanceRequestActions(
                        params,
                        () => { if (!succeeded) setApproveBtnLoader(false); },
                        (loading) => {
                                setApproveBtnLoader(loading);
                                if (!loading) {
                                        setSelectedId([]);
                                        onClose();
                                }
                        },
                        () => { succeeded = true; successCallback(); }
                )
        };

        const handleReject = (commonId) => {
                const targetRequestIds = selectedId && selectedId.length > 0
                        ? selectedId
                        : (actionableItems.map(item => item?.id).filter(Boolean) || []);

                let params = {
                        action: 'Rejected',
                        commonId: commonId,
                        requestIds: targetRequestIds,
                        requestType: 'Leave'
                };
                if (data?.userId || data?.user?.id) {
                        params.userId = data?.userId || data?.user?.id;
                }

                let succeeded = false;

                setRejectBtnLoader(true);
                leaveOrAttendanceRequestActions(
                        params,
                        () => { if (!succeeded) setRejectBtnLoader(false); },
                        (loading) => {
                                setRejectBtnLoader(loading);
                                if (!loading) {
                                        setSelectedId([]);
                                        onClose();
                                }
                        },
                        () => { succeeded = true; successCallback(); }
                )
        };

        // const checkOnChange = (e, item) => {
        //         setSelectedDates(e.target.checked);
        //         setSelectedId(prevSelectedId => prevSelectedId ? [...prevSelectedId, item?.id] : [item?.id]);
        // };
        const checkOnChange = (e, item) => {
                const checked = e.target.checked;
                setSelectedId(prev => {
                        const current = Array.isArray(prev) ? prev : [];
                        if (checked) {
                                return current.includes(item?.id) ? current : [...current, item?.id];
                        } else {
                                return current.filter(id => id !== item?.id);
                        }
                });
        };

        const getItemStatusBadge = (item) => {
                const rawStatus = item?.overAllStatus ?? item?.overallStatus ?? item?.status ?? data?.overAllStatus ?? data?.overallStatus ?? data?.status;
                if (!rawStatus) return null;

                const normalized = String(rawStatus).trim().toLowerCase();

                let color = '#28DF99';
                let borderColor = '#28DF99';
                let backgroundColor = '#D5F8EF';
                let text = 'APPROVED';

                if (normalized === 'approved') {
                        color = '#28DF99';
                        borderColor = '#28DF99';
                        backgroundColor = '#D5F8EF';
                        text = 'APPROVED';
                } else if (normalized === 'rejected') {
                        color = '#FF414D';
                        borderColor = '#FF414D';
                        backgroundColor = '#FBE7E9';
                        text = 'REJECTED';
                } else if (normalized === 'pending') {
                        color = '#DAAB00';
                        borderColor = '#FFA931';
                        backgroundColor = '#FDF3CE';
                        text = 'PENDING';
                } else if (normalized === 'partially approved' || normalized === 'partially_approved') {
                        color = '#0AB1E6';
                        borderColor = '#0AB1E6';
                        backgroundColor = '#D8F6FF';
                        text = 'PARTIALLY APPROVED';
                } else if (normalized === 'partially rejected' || normalized === 'partially_rejected') {
                        color = '#FF414D';
                        borderColor = '#FF414D';
                        backgroundColor = '#FBE7E9';
                        text = 'PARTIALLY REJECTED';
                } else {
                        color = '#636363';
                        borderColor = '#D9D9D9';
                        backgroundColor = '#F5F5F5';
                        text = String(rawStatus).replace(/_/g, ' ').toUpperCase();
                }

                return (
                        <span
                                style={{
                                        marginLeft: 16,
                                        padding: '1px 8px',
                                        border: `1px solid ${borderColor}`,
                                        borderRadius: '4px',
                                        color: color,
                                        backgroundColor: backgroundColor,
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        lineHeight: '14px',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        letterSpacing: '0.2px'
                                }}
                        >
                                {text}
                        </span>
                );
        };

        return (
                <Modal
                        centered
                        closable
                        open={open}
                        footer={null}
                        className='modal-round-corner'
                        onCancel={onClose}
                >
                        <center><Heading>Details</Heading></center>
                        <div className='p-20'>
                                <Row style={{ justifyContent: 'space-between', width: '80%' }}>
                                        <Col>
                                                <div className='simgrey bold-600'>Leave type</div>
                                                <div>{data?.leaveType}</div>
                                        </Col>
                                        <Col>
                                                <div className='simgrey bold-600'>Duration</div>
                                                <div>{data?.duration} {Number(data?.duration) > 1 ? 'days' : 'day'}</div>
                                        </Col>
                                </Row>
                                <div className='m-t-20'>
                                        <div className='simgrey bold-600'>Reason(s)</div>
                                        <div>{data?.reason}</div>
                                </div>

                                <div className='m-t-50 simgrey bold-600'>Date(s)</div>
                                {requestList.map((item, index) => {
                                        const hasCheckbox = canPerformActions && !isDateCheckboxDisabled(item);

                                        return (
                                                <div className={`m-t-20 ${hasCheckbox ? 'm-l-10' : ''}`} key={item?.id || index}>
                                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                {hasCheckbox && (
                                                                        <Checkbox
                                                                                checked={selectedId?.includes(item?.id)}
                                                                                onChange={(e) => checkOnChange(e, item)}
                                                                                style={{ marginTop: 0 }}
                                                                        />
                                                                )}
                                                                <div className={`${hasCheckbox ? 'm-l-20' : ''} color-black text-sm bold-600`}>
                                                                        {moment(item?.date || item?.startDate).format('DD/MM/YYYY')}
                                                                </div>
                                                                {getItemStatusBadge(item)}
                                                        </div>
                                                        <div className='simgrey text-xs bold-600' style={{ marginLeft: hasCheckbox ? 37 : 0, marginTop: 4 }}>
                                                                {item?.duration == '0.5' || data?.duration == '0.5' ? 'Half day' : 'Full day'}
                                                        </div>
                                                </div>
                                        );
                                })}

                                {canPerformActions && hasActionableDates && (
                                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 30 }}>
                                                {allApproved ? (
                                                        <Button
                                                                disabled={isRejectDisabled}
                                                                onClick={() => handleReject(data?.commonId)}
                                                                type='primary'
                                                                style={{ backgroundColor: '#FF414D', border: 'none', minWidth: 140 }}
                                                                loading={rejectBtnLoader}
                                                        >
                                                                REJECT
                                                        </Button>
                                                ) : allRejected ? (
                                                        <Button
                                                                disabled={isApproveDisabled}
                                                                onClick={() => handleApprove(data?.commonId)}
                                                                type='primary'
                                                                style={{ backgroundColor: '#28DF99', border: 'none', minWidth: 140 }}
                                                                loading={approveBtnLoader}
                                                        >
                                                                APPROVE
                                                        </Button>
                                                ) : (
                                                        <div className='r-jsb' style={{ width: '60%', gap: 16 }}>
                                                                <Button
                                                                        disabled={isRejectDisabled}
                                                                        onClick={() => handleReject(data?.commonId)}
                                                                        type='primary'
                                                                        style={{ backgroundColor: '#FF414D', border: 'none', flex: 1 }}
                                                                        loading={rejectBtnLoader}
                                                                >
                                                                        REJECT
                                                                </Button>
                                                                <Button
                                                                        disabled={isApproveDisabled}
                                                                        onClick={() => handleApprove(data?.commonId)}
                                                                        type='primary'
                                                                        style={{ backgroundColor: '#28DF99', border: 'none', flex: 1 }}
                                                                        loading={approveBtnLoader}
                                                                >
                                                                        APPROVE
                                                                </Button>
                                                        </div>
                                                )}
                                        </div>
                                )}
                        </div>
                </Modal>
        );
};

const mapStateToProps = (state) => {
        const { user } = state?.session || {};
        const { } = state?.profileNew || {};
        const { } = state?.userManagementV2 || {};
        return { user };
};

const mapDispatchToProps = (dispatch) => ({
        leaveOrAttendanceRequestActions: (params, callback, pagecallback, successCallback) => dispatch(leaveOrAttendanceRequestActions(params, callback, pagecallback, successCallback)),
});

export default connect(mapStateToProps, mapDispatchToProps)(LeaveRequestsModal);